import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { libraryAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchBooks = createAsyncThunk('library/books', async (params, { rejectWithValue }) => {
  try {
    const response = await libraryAPI.fetchBooks(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch books'));
  }
});

export const createBook = createAsyncThunk('library/createBook', async (data, { rejectWithValue }) => {
  try {
    const response = await libraryAPI.createBook(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create book'));
  }
});

export const updateBook = createAsyncThunk('library/updateBook', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await libraryAPI.updateBook(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to update book'));
  }
});

export const deleteBook = createAsyncThunk('library/deleteBook', async (id, { rejectWithValue }) => {
  try {
    await libraryAPI.deleteBook(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete book'));
  }
});

export const borrowBook = createAsyncThunk('library/borrow', async (data, { rejectWithValue }) => {
  try {
    const response = await libraryAPI.borrowBook(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to borrow book'));
  }
});

export const returnBook = createAsyncThunk('library/return', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await libraryAPI.returnBook(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to return book'));
  }
});

const initialState = {
  books: [],
  currentBook: null,
  borrowings: [],
  overdueBooks: [],
  stats: null,
  loading: false,
  error: null,
};

const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    clearLibraryError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBooks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchBooks.fulfilled, (state, action) => { state.loading = false; state.books = action.payload.data || action.payload; })
      .addCase(fetchBooks.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createBook.fulfilled, (state, action) => { state.books.unshift(action.payload); })
      .addCase(updateBook.fulfilled, (state, action) => { state.books = state.books.map((b) => b.id === action.payload.id ? action.payload : b); })
      .addCase(deleteBook.fulfilled, (state, action) => { state.books = state.books.filter((b) => b.id !== action.payload); })
      .addCase(borrowBook.fulfilled, (state, action) => { state.borrowings.unshift(action.payload); })
      .addCase(returnBook.fulfilled, (state, action) => { state.borrowings = state.borrowings.map((b) => b.id === action.payload.id ? action.payload : b); });
  },
});

export const { clearLibraryError } = librarySlice.actions;
export default librarySlice.reducer;
